/**
 *  图层高亮操作
 */
import FeatureLayer from '../feature-layer/FeatureLayer';
import {
    Cartesian2,
    Cartographic,
    Math,
    ImageryLayerFeatureInfo,
    Color,
    Cesium3DTileFeature,
    HorizontalOrigin,
    VerticalOrigin,
} from 'cesium';
import CircleRippleMaterialProperty from '../map/material/CircleRippleMaterialProperty';
import Marker from '../marker/Marker';
import LineFlowLayer from '../line-flow-layer/LineFlowLayer';
import transformRgb from '../_util/transformRgb';
import RoadLineLayer from '../road-line-layer/RoadLineLayer';

/**
 * @description:  wms图层点击获取的数据处理
 * @param {*} data
 * @return {*}
 */
const handleData = (data, map) => {
    let _data = [];
    let datas = Array.isArray(data) ? data : [data];
    datas.map(v => {
        // 影像图层的数据
        if (v instanceof ImageryLayerFeatureInfo || v?.data?.geometry?.coordinates) {
            let item = v.data || {};
            let { geometry } = item;
            let { coordinates = [] } = geometry || {};
            _data.push({ ...item, geometryInfo: { ...geometry, lngLats: coordinates } });
        } else {
            if (v?.position?._value) {
                handlePositionData({ data: _data, position: v?.position?._value, map, feature: v });
            } else if (v?._polyline?._positions?._value) {
                handlePositionData({
                    data: _data,
                    position: v?._polyline?._positions?._value,
                    map,
                    feature: v,
                    type: 'Polyline',
                });
            } else if (v?._polygon?._hierarchy?._value?.positions) {
                handlePositionData({
                    data: _data,
                    position: v?._polygon?._hierarchy?._value?.positions,
                    map,
                    feature: v,
                    type: 'Polygon',
                });
            }
        }
    });
    return _data;
};

const handlePositionData = ({ data, position, map, feature, type = 'point' }) => {
    let lngLats = [];
    if (Array.isArray(position)) {
        position.map(v => {
            lngLats.push(getLngLats(v));
        });
    } else {
        // 点
        lngLats = getLngLats(position);
    }

    data.push({
        ...map.getProperties(feature),
        geometryInfo: {
            type: type,
            lngLats,
        },
    });
};

const getLngLats = position => {
    let lngLats = [];

    let cartographic = Cartographic.fromCartesian(position);
    let lng = Math.toDegrees(cartographic?.longitude);
    lngLats.push(lng);
    let lat = Math.toDegrees(cartographic?.latitude);
    lngLats.push(lat);

    return lngLats;
};

const getPolygonStyle = style => {
    const { fill = {}, stroke = {}, color, ...otherStyle } = style || {};
    return {
        type: 'Polygon',
        style: {
            stroke: {
                color: color || '#f7ff01',
                opacity: 1,
                width: 2,
                ...stroke,
            },
            fill: {
                color: color || '#f7ff01',
                opacity: 0.35,
                ...fill,
            },
        },
        ...otherStyle,
    };
};

const getMarkerStyle = (style) => {
    return {
        type: 'Marker',
        style: {
            type: 'CommonIcon2',
            ...style
        },
    }
}
const getLineStyle = style => {
    const { color } = style || {};

    return {
        type: 'Polyline',
        style: {
            stroke: {
                color: color || '#f7ff01',
                opacity: 1,
                width: 5,
            },
        },
    };
};

const pointStyle = style => {
    const { color } = style || {};
    return {
        style: {
            type: 'PointIcon',
            color: color || '#f7ff01',
            size: 10,
            horizontalOrigin: HorizontalOrigin.CENTER,
            verticalOrigin: VerticalOrigin.BOTTOM,
            pixelOffset: new Cartesian2(0, 10),
        },
    };
};

export default class HighlightLayer {
    constructor(options) {
        const { isFit = true, color } = options;

        this.map = options.map;
        this.layer = new FeatureLayer({
            style: getLineStyle(this.map.dark),
        });

        this.map.dataSources.add(this.layer);

        this.color = color || '#f7ff01';

        this.primitiveLayer = null;
    }

    // 高亮图层增加feature
    addHighlightFeature = (feature, style = {}) => {
        if (feature && !feature.hideHighlight && !this.map.hideHighlight) {
            this.clearHighlight();
            if (feature instanceof Cesium3DTileFeature) {
                return this.addHighlight3DTileFeature(feature, this.color);
            } else if (feature.primitive) {
                return this.addHighlightPrimitive(feature);
            } else {
                let type = feature?.data?.geometry?.type;
                if (feature instanceof Marker) {
                    this.layer.style = getMarkerStyle(feature._billboard.mapIcon);
                }  else if (['LineString', 'MultiLineString'].includes(type)) {
                    this.layer.style = getLineStyle({ color: this.color });
                } else if (['Polygon', 'MultiPolygon'].includes(type)) {
                    this.layer.style = getPolygonStyle({ ...style, color: this.color });
                } else if (['Point'].includes(type)) {
                    this.layer.style = pointStyle({ color: this.color });
                } else if (feature?._polyline?._positions?._value) {
                    this.layer.style = getLineStyle(this.map.dark);
                } else if (feature?._polygon?._hierarchy?._value?.positions) {
                    this.layer.style = getPolygonStyle(style);
                } else {
                    this.layer.style = pointStyle({ color: this.color });
                }
                let data = handleData(feature, this.map, this.layer);
                this.layer.data = data;
                this.layer.loadData(data);
            }
        }
    };

    addHighlight3DTileFeature = (feature, color) => {
        if (this.feature === feature && feature !== undefined) {
            return;
        }

        if (this.feature && this.oldColor && !this.feature.content.isDestroyed()) {
            this.feature.color = this.oldColor;
        }

        this.oldColor = Color.clone(feature.color);

        feature.color = Color.fromCssColorString(transformRgb(color || '#f7ff01', 0.3));

        this.feature = feature;
    };

    clear3DTileFeature() {
        if (this.oldColor && this.feature) {
            this.feature.color = this.oldColor;
        }

        this.feature = null;
        this.oldColor = null;
    }

    addHighlightPrimitive = feature => {
        if (feature.primitive instanceof LineFlowLayer || feature.primitive instanceof RoadLineLayer) {
            this.clearPrimitive();
            const data = [feature.values_];
            this.primitiveLayer = new LineFlowLayer({
                fadeOutColor: this.color,
                data: [feature.values_],
            });
            this.map.scene.primitives.add(this.primitiveLayer);
            this.map.scene.primitives.raiseToTop(this.primitiveLayer);
            this.layer.data = data;
            this.layer.loadData(data);
        }
    };

    clearPrimitive() {
        if (this.primitiveLayer) {
            if (this.map.scene.primitives.contains(this.primitiveLayer)) {
                this.map.scene.primitives.remove(this.primitiveLayer);
            }
            this.primitiveLayer = null;
        }
    }

    clearHighlight() {
        this.clearPrimitive();
        this.clear3DTileFeature();
    }

    clearFeatures() {
        this.layer.entities.removeAll();
        this.clearHighlight();
    }

    destroy() {
        this.map.dataSources.remove(this.layer);
        this.clearHighlight();
    }
}
